import Dexie from 'dexie';
import moment from 'moment';
import {
    getUserLastCollectionDate,
    getSearchesEntity,
    addNewSearchesEntity,
    updateSearchEntity
} from '../../features/Personalization/service/indexDBSearchesService';
import {
    allowPersonalization,
    calcDaysDiff,
    getDeserializedFilters,
    findIndexedSearch,
    isPopularSearch
} from '../../features/Personalization/utils/personalization';
import {SEARCH_NAME_MAX_LENGTH} from '../../_legacy/Searches/consts/searches';
import {showSearchesSuggestion} from '../../features/Personalization/actions';
import {Search} from '../../_legacy/Searches/stores/Search';
import {isDevOrPrisoft} from '../../utils/platform';
import {configuration as baseConfiguration} from '../../features/Personalization/config';

export type SearchFilter = {
    field?: string;
    fieldName: string;
    sort: string;
    isdesc: string;
    operator: number;
    op?: string;
    fromval?: string;
    toval?: string;
    values?: string[];
    value?: {min: string; max: string};
    valType?: string;
};

export type IndexedSearch = {
    count: number;
    filters: SearchFilter[];
};

export interface User {
    userName: string;
    lang: number;
    formName: string;
    company: string;
}

export interface Blacklist extends User {
    searches?: {[index: number]: SearchFilter[][]};
}

export interface Entity extends User {
    collectionDate: string;
    searches?: {[index: number]: IndexedSearch[]};
}

interface CollectionDates {
    userName: string;
    collectionDates?: string[];
}

export const configuration = {
    ...baseConfiguration,
    days: isDevOrPrisoft() ? 10 : 45,
    minSearches: 3,
    maxValuesToDisplay: 3,
    maxSearchNameLength: SEARCH_NAME_MAX_LENGTH
};

export class PersonalizationSearches extends Dexie {
    public collectionDates: Dexie.Table<CollectionDates>;
    public searches: Dexie.Table<Entity>;

    constructor() {
        super('PersonalizationSearches');
        this.version(1).stores({
            collectionDates: 'userName, collectionDates',
            searches: '[userName+lang+company+formName+collectionDate], searches'
        });
    }
}

export const db = new PersonalizationSearches();

export const addSearchEntry = async (search: Search) => {
    if (!allowPersonalization()) return;

    const {filters, store} = search;
    const {form} = store;

    if (form.parentForm) return;

    const currentSearchFilters: SearchFilter[] = getDeserializedFilters(filters, form)
        .slice()
        .sort((a, b) => (a.fieldName > b.fieldName ? 1 : -1));

    const searchFiltersIndex = currentSearchFilters.length;

    if (!searchFiltersIndex) return;
    if (searchFiltersIndex === 1 && currentSearchFilters[0].values?.[0] === '*') return;

    const lastCollectionDate = await getUserLastCollectionDate(db);

    const entityToUpdate = await getSearchesEntity(db, form.name, lastCollectionDate);

    if (!entityToUpdate) {
        await addNewSearchesEntity(
            db,
            form.name,
            searchFiltersIndex,
            currentSearchFilters,
            lastCollectionDate
        );
    } else {
        await updateSearchEntity(db, entityToUpdate, searchFiltersIndex, currentSearchFilters);

        const userCollectionDate = moment(lastCollectionDate, 'DD/MM/YYYY');
        const daysDiff = calcDaysDiff(userCollectionDate);
        const shouldExecute = daysDiff >= configuration.days;

        if (shouldExecute) {
            // 1. Check if there is empty place in list
            if (!search.canSave) return;
            // 2. Get the updtaed Entity
            const updatedEntity = await getSearchesEntity(db, form.name, lastCollectionDate);
            // 3. Find search in updatedEntity
            const searchInSearchesDB = findIndexedSearch(updatedEntity, currentSearchFilters);
            if (!searchInSearchesDB || searchInSearchesDB.count < configuration.minSearches) return;
            // 4. Check if search is popular
            const isMostPopularSearch = await isPopularSearch(
                updatedEntity,
                searchInSearchesDB,
                configuration,
                form.name
            );
            if (!isMostPopularSearch) return;
            // 5. Show dialog
            showSearchesSuggestion(db, search, form, searchInSearchesDB);
        }
    }
};
